import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiPlay, FiPause, FiVolume2, FiVolumeX } from 'react-icons/fi'

interface VideoThumbnailProps {
  videoUrl: string
  className?: string
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  onPlay?: () => void
  onPause?: () => void
  onError?: () => void
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  videoUrl,
  className = '',
  autoPlay = false,
  loop = true,
  muted = true,
  onPlay,
  onPause,
  onError,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(muted)
  const [isVisible, setIsVisible] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.1 }
    )

    const currentContainer = containerRef.current
    if (currentContainer) {
      observer.observe(currentContainer)
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer)
      }
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const syncState = () => {
      setIsPlaying(!video.paused)
      setIsMuted(video.muted)
    }

    const handlePlayback = async () => {
      if (isVisible && autoPlay) {
        video.muted = true
        setIsMuted(true)
        try {
          await video.play()
        } catch (error) {
          console.error('Autoplay was prevented.', error)
          setIsPlaying(false)
        }
      } else if (!isVisible) {
        video.pause()
      }
    }

    handlePlayback()

    video.addEventListener('play', syncState)
    video.addEventListener('pause', syncState)
    video.addEventListener('volumechange', syncState)

    return () => {
      video.removeEventListener('play', syncState)
      video.removeEventListener('pause', syncState)
      video.removeEventListener('volumechange', syncState)
    }
  }, [autoPlay, videoUrl, isVisible])

  const handlePlayPause = async () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      try {
        await video.play()
      } catch (err) {
        console.error('Failed to play video:', err)
      }
    } else {
      video.pause()
    }
  }

  const handleMuteToggle = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
  }

  return (
    <div ref={containerRef} className={`relative group ${className}`}>
      <video
        key={videoUrl}
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-cover"
        loop={loop}
        muted={muted}
        playsInline
        preload="auto"
        crossOrigin="anonymous"
        onPlay={onPlay}
        onPause={onPause}
        onError={(e) => {
          console.error('Video error:', (e.target as HTMLVideoElement).error)
          onError?.()
        }}
      />

      {/* Video Controls - Bottom Right */}
      {autoPlay && (
        <div className="absolute bottom-6 right-6 flex items-center space-x-3 z-10">
          {/* Play/Pause Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePlayPause}
            className="w-12 h-12 bg-black bg-opacity-70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-opacity-90 transition-all duration-200 shadow-lg cursor-pointer"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <FiPause size={20} /> : <FiPlay className="ml-0.5" size={20} />}
          </motion.button>

          {/* Mute/Unmute Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleMuteToggle}
            className="w-12 h-12 bg-black bg-opacity-70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-opacity-90 transition-all duration-200 shadow-lg cursor-pointer"
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <FiVolumeX size={20} /> : <FiVolume2 size={20} />}
          </motion.button>
        </div>
      )}
    </div>
  )
}

export default React.memo(VideoThumbnail)
